import axios from 'axios';
import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';
import { API_ROUTES } from '@/config/api-routes';
import { appendCriteriaContains, appendPageable } from '@/shared/utils/spring-api';
import { defaultValue } from '../model/branch.model';
import { serializeAxiosError, resolveAsyncThunkErrorMessage } from './reducer.utils';
const apiUrl = API_ROUTES.branches;
const initialState = {
    loading: false,
    errorMessage: null,
    branches: [],
    branch: defaultValue,
    updating: false,
    updateSuccess: false,
    totalItems: 0,
};
export const getBranches = createAsyncThunk('branchManagement/fetch_branches', async ({ page, size, sort, filter }) => {
    const q = new URLSearchParams();
    appendPageable(q, page, size, sort);
    appendCriteriaContains(q, 'branchName', filter?.trim());
    return axios.get(`${apiUrl}?${q.toString()}`);
}, { serializeError: serializeAxiosError });
export const getBranch = createAsyncThunk('branchManagement/fetch_branch', async (id) => axios.get(`${apiUrl}/${id}`), { serializeError: serializeAxiosError });
export const getBranchCount = createAsyncThunk('branchManagement/fetch_branch_count', async () => axios.get(`${apiUrl}/count`), { serializeError: serializeAxiosError });
export const createBranch = createAsyncThunk('branchManagement/create_branch', async (entity) => axios.post(apiUrl, entity), { serializeError: serializeAxiosError });
export const updateBranch = createAsyncThunk('branchManagement/update_branch', async (entity) => {
    const result = await axios.put(`${apiUrl}/${entity.id}`, entity);
    return result;
}, { serializeError: serializeAxiosError });
export const patchBranch = createAsyncThunk('branchManagement/patch_branch', async (entity) => {
    const { id, ...partial } = entity;
    const result = await axios.patch(`${apiUrl}/${id}`, partial);
    return result;
}, { serializeError: serializeAxiosError });
export const deleteBranch = createAsyncThunk('branchManagement/delete_branch', async (id) => axios.delete(`${apiUrl}/${id}`), { serializeError: serializeAxiosError });
export const BranchManagementSlice = createSlice({
    name: 'branchManagement',
    initialState: initialState,
    reducers: {
        reset() {
            return initialState;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getBranch.fulfilled, (state, action) => {
            state.loading = false;
            state.branch = action.payload.data;
        })
            .addCase(getBranchCount.fulfilled, (state, action) => {
            state.totalItems = action.payload.data;
        })
            .addCase(deleteBranch.fulfilled, (state) => {
            state.updating = false;
            state.updateSuccess = true;
            state.branch = defaultValue;
        })
            .addMatcher(isFulfilled(getBranches), (state, action) => {
            state.loading = false;
            state.branches = action.payload.data;
            const raw = action.payload.headers['x-total-count'];
            if (raw != null) {
                state.totalItems = parseInt(raw, 10);
            }
        })
            .addMatcher(isFulfilled(createBranch, updateBranch, patchBranch), (state, action) => {
            state.updating = false;
            state.loading = false;
            state.updateSuccess = true;
            state.branch = action.payload.data;
        })
            .addMatcher(isPending(getBranches, getBranch), (state) => {
            state.errorMessage = null;
            state.updateSuccess = false;
            state.loading = true;
        })
            .addMatcher(isPending(createBranch, updateBranch, patchBranch, deleteBranch), (state) => {
            state.errorMessage = null;
            state.updateSuccess = false;
            state.updating = true;
        })
            .addMatcher(isRejected(getBranches, getBranch, getBranchCount, createBranch, updateBranch, patchBranch, deleteBranch), (state, action) => {
            state.loading = false;
            state.updating = false;
            state.updateSuccess = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        });
    },
});
export const { reset } = BranchManagementSlice.actions;
export default BranchManagementSlice.reducer;
